import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { parkingApi, useCurrentShift, usePkList, usePkMutation, type PkShift } from "../api/parkingApi";
import { ConsolePage, DetailRow, Drawer, FilterSelect, KpiRow, KpiTile, LoadState, Panel, StatusChip, ToolButton, formatDateTime } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { LotSelect, METHOD_ICON, errorText, money } from "./parkingUi";
import { buildShiftPdf, openPdf } from "./parkingPdf";

// ===========================================================================
// Control de estacionamientos → Cortes de caja: corte abierto del cajero
// (abrir con fondo, cerrar con efectivo contado), historial por turno y cajero
// con totales por método, efectivo esperado, diferencia y resumen imprimible.
// ===========================================================================

export default function ShiftsScreen() {
  const { t, i18n } = useTranslation("parking");
  const [lot, setLot] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<PkShift | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState<PkShift | null>(null);
  const lotsQ = usePkList("lots");
  const currentQ = useCurrentShift();
  const q = usePkList("shifts", { lot_id: lot || undefined, status: status || undefined, limit: 300 });
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const current = currentQ.data ?? null;
  const reportQ = usePkList("payments", selected ? { shift_id: selected.id, limit: 500 } : undefined, { enabled: Boolean(selected) });

  const openShift = usePkMutation(parkingApi.openShift);
  const closeShift = usePkMutation(({ id, counted, notes }: { id: string; counted: number; notes?: string }) => parkingApi.closeShift(id, { counted_cash: counted, notes }));

  const closed = rows.filter((s) => s.status === "closed");
  const totalWeek = closed.slice(0, 50).reduce((a, s) => a + (s.totals?.total ?? 0), 0);
  const diffs = closed.filter((s) => (s.difference ?? 0) !== 0);

  const print = async (s: PkShift) => {
    try {
      const report = await parkingApi.shiftReport(s.id);
      openPdf(buildShiftPdf({ report, t, lang: i18n.language }), `corte-${s.number}.pdf`);
    } catch (err) {
      toast.error(errorText(err, t("common.error")));
    }
  };

  return (
    <ConsolePage
      icon="ri-safe-2-line"
      title={t("shifts.title")}
      subtitle={t("shifts.subtitle")}
      actions={!current ? <ToolButton icon="ri-door-open-line" variant="primary" onClick={() => setOpenModal(true)}>{t("shifts.open")}</ToolButton> : <ToolButton icon="ri-door-closed-line" variant="danger" onClick={() => setCloseModal(current)}>{t("shifts.close")}</ToolButton>}
    >
      {current && (
        <Panel title={t("shifts.current", { interpolation: { escapeValue: false }, number: current.number })} icon="ri-user-star-line">
          <div className="grid gap-3 sm:grid-cols-4">
            <DetailRow label={t("common.lot")}>{current.lot_name}</DetailRow>
            <DetailRow label={t("shifts.openedAt")}>{formatDateTime(current.opened_at, i18n.language)}</DetailRow>
            <DetailRow label={t("shifts.openingFloat")}>{money(current.opening_float, i18n.language)}</DetailRow>
            <DetailRow label={t("shifts.total")}>{money(current.running_total, i18n.language)} · {current.running_count ?? 0}</DetailRow>
          </div>
        </Panel>
      )}
      <KpiRow>
        <KpiTile icon="ri-safe-2-line" tone="primary" label={t("shifts.closedN")} value={closed.length} />
        <KpiTile icon="ri-door-open-line" tone="success" label={t("shifts.openN")} value={rows.filter((s) => s.status === "open").length} />
        <KpiTile icon="ri-money-dollar-circle-line" tone="info" label={t("shifts.collected")} value={money(totalWeek, i18n.language)} />
        <KpiTile icon="ri-scales-3-line" tone={diffs.length ? "warning" : "success"} label={t("shifts.withDifference")} value={diffs.length} hint={money(diffs.reduce((a, s) => a + (s.difference ?? 0), 0), i18n.language)} />
      </KpiRow>
      <div className="flex flex-wrap items-center gap-2">
        <LotSelect lots={lotsQ.data ?? []} value={lot} onChange={setLot} />
        <FilterSelect value={status} onChange={setStatus} label={t("common.status")} options={[{ value: "", label: t("common.allStatuses") }, { value: "open", label: t("shifts.status.open") }, { value: "closed", label: t("shifts.status.closed") }]} />
      </div>
      <LoadState loading={q.isLoading} error={q.error} empty={!rows.length} emptyText={t("shifts.empty")} onRetry={() => q.refetch()}>
        <div className="overflow-x-auto rounded-xl border border-line bg-card">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">{t("shifts.cashier")}</th>
                <th className="px-3 py-2">{t("common.lot")}</th>
                <th className="px-3 py-2">{t("shifts.period")}</th>
                <th className="px-3 py-2 text-right">{t("method.cash")}</th>
                <th className="px-3 py-2 text-right">{t("method.card_terminal")}</th>
                <th className="px-3 py-2 text-right">{t("shifts.total")}</th>
                <th className="px-3 py-2 text-right">{t("shifts.difference")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-subtle" onClick={() => setSelected(s)}>
                  <td className="px-3 py-2 tabular-nums text-content-secondary">{s.number}</td>
                  <td className="px-3 py-2 text-content">{s.cashier_name}</td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-content-secondary">{s.lot_name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-content-secondary">{formatDateTime(s.opened_at, i18n.language)}{s.closed_at ? ` → ${new Date(s.closed_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}` : ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-content">{money(s.totals?.by_method.cash ?? 0, i18n.language)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-content">{money(s.totals?.by_method.card_terminal ?? 0, i18n.language)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-content">{money(s.totals?.total ?? s.running_total ?? 0, i18n.language)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${(s.difference ?? 0) < 0 ? "text-error" : (s.difference ?? 0) > 0 ? "text-warning" : "text-content-secondary"}`}>{s.status === "closed" ? money(s.difference ?? 0, i18n.language) : "—"}</td>
                  <td className="px-3 py-2"><StatusChip tone={s.status === "open" ? "success" : "neutral"} pulse={s.status === "open"}>{t(`shifts.status.${s.status}`)}</StatusChip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>

      <Drawer
        open={Boolean(selected)}
        title={selected ? `${t("shifts.shift")} #${selected.number}` : ""}
        subtitle={selected ? `${selected.cashier_name} · ${selected.lot_name}` : ""}
        onClose={() => setSelected(null)}
        footer={selected && <ToolButton icon="ri-printer-line" variant="primary" onClick={() => print(selected)}>{t("shifts.print")}</ToolButton>}
      >
        {selected && (
          <div>
            <DetailRow label={t("shifts.openedAt")}>{formatDateTime(selected.opened_at, i18n.language)}</DetailRow>
            <DetailRow label={t("shifts.closedAt")}>{selected.closed_at ? formatDateTime(selected.closed_at, i18n.language) : "—"}</DetailRow>
            <DetailRow label={t("shifts.openingFloat")}>{money(selected.opening_float, i18n.language)}</DetailRow>
            {selected.totals && (
              <>
                {(["cash", "card_terminal", "stripe", "link"] as const).map((m) => (
                  <DetailRow key={m} label={t(`method.${m}`)}>{money(selected.totals!.by_method[m], i18n.language)}</DetailRow>
                ))}
                <DetailRow label={t("shifts.expectedCash")}>{money(selected.totals.expected_cash, i18n.language)}</DetailRow>
                <DetailRow label={t("shifts.countedCash")}>{money(selected.counted_cash, i18n.language)}</DetailRow>
                <DetailRow label={t("shifts.difference")}><span className={(selected.difference ?? 0) < 0 ? "text-error" : ""}>{money(selected.difference, i18n.language)}</span></DetailRow>
              </>
            )}
            {selected.close_notes && <p className="mt-2 text-xs text-content-muted">{selected.close_notes}</p>}
            <h3 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-content-muted">{t("shifts.payments")} ({reportQ.data?.length ?? 0})</h3>
            <ul className="divide-y divide-line rounded-lg border border-line">
              {(reportQ.data ?? []).slice(0, 60).map((p) => (
                <li key={p.id} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                  <i className={`${METHOD_ICON[p.method]} text-content-muted`} />
                  <span className="min-w-0 flex-1 truncate font-mono text-content">{p.folio ?? p.holder_name}</span>
                  <span className="tabular-nums text-content">{money(p.amount, i18n.language)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Drawer>

      {openModal && (
        <FormModal
          title={t("shifts.open")}
          submitLabel={t("shifts.open")}
          busy={openShift.isPending}
          onClose={() => setOpenModal(false)}
          initial={{ lot_id: lotsQ.data?.[0]?.id ?? "", opening_float: 500 }}
          onSubmit={async (v: FormValues) => {
            try {
              await openShift.mutateAsync({ lot_id: String(v.lot_id), opening_float: Number(v.opening_float || 0) });
              toast.success(t("shifts.opened"));
              setOpenModal(false);
            } catch (err) {
              toast.error(errorText(err, t("common.saveError")));
            }
          }}
          fields={[
            { key: "lot_id", label: t("common.lot"), type: "select", required: true, options: (lotsQ.data ?? []).map((l) => ({ value: l.id, label: l.name })) },
            { key: "opening_float", label: t("shifts.openingFloat"), type: "number", min: 0, required: true, hint: t("shifts.openingFloatHint") },
          ]}
        />
      )}
      {closeModal && (
        <FormModal
          title={t("shifts.closeTitle", { interpolation: { escapeValue: false }, number: closeModal.number })}
          submitLabel={t("shifts.close")}
          busy={closeShift.isPending}
          onClose={() => setCloseModal(null)}
          onSubmit={async (v: FormValues) => {
            try {
              const out = await closeShift.mutateAsync({ id: closeModal.id, counted: Number(v.counted_cash || 0), notes: String(v.notes || "") });
              toast.success(t("shifts.closed", { interpolation: { escapeValue: false }, diff: money(out.difference, i18n.language) }));
              setCloseModal(null);
              await print(out);
            } catch (err) {
              toast.error(errorText(err, t("common.saveError")));
            }
          }}
          fields={[
            { key: "counted_cash", label: t("shifts.countedCash"), type: "number", min: 0, required: true, hint: t("shifts.countedHint") },
            { key: "notes", label: t("shifts.notes"), type: "textarea" },
          ]}
        />
      )}
    </ConsolePage>
  );
}
